/**
 * evaluateFormula
 *
 * Pure formula evaluation for ArTable computed columns.
 * Uses expr-eval-fork for mathematical expression parsing.
 * Two INDEPENDENT module-level structures are keyed by arbitrary user formula
 * text — the parsed-expression cache and the alarmed-formula dedupe set — so
 * both are BOUNDED at FORMULA_KEYED_SET_LIMIT and cleared wholesale on reaching
 * it: this module runs on a server hot path (one repair pass reconciles every
 * row of a table), where an unbounded map is a leak and a per-row
 * `console.error` is a log flood. A failing formula therefore logs ONCE per
 * process until the alarm set clears, and an evicted expression re-parses on
 * next use — the pre-cache cost.
 */

import { Parser } from 'expr-eval-fork';
import { readArCellOperand } from './readArCellOperand';
import type { ArCell, ArCellValue } from '../System.Artifacts.types';

/**
 * The fork carries `binaryOps` and `ternaryOps` on every instance but omits both
 * from its shipped `.d.ts`. Declaring them is what lets `formulaOperatorNames`
 * read the tokenizer's own maps instead of restating them by hand. The
 * declaration asserts a runtime shape the compiler cannot verify, so every read
 * below is `?? {}`: a fork that renamed either map would otherwise throw inside
 * `Object.keys(undefined)` during module evaluation of a file EVERY `#types`
 * barrel import pulls in — the app would fail to boot with no compile error.
 */
declare module 'expr-eval-fork' {
  interface Parser {
    binaryOps: Record<string, unknown>;
    ternaryOps: Record<string, unknown>;
  }
}

const parser = new Parser();

type Expression = ReturnType<typeof parser.parse>;
type CachedFormula = { parsed: Expression; variables: string[] };

/** The shared cap on every structure keyed by raw formula text, cache and alarm set alike. */
const FORMULA_KEYED_SET_LIMIT = 1000;

const expressionCache = new Map<string, CachedFormula>();
const alarmedFormulas = new Set<string>();

/**
 * Parses a formula and caches both the parsed expression and its variables.
 * Use at config time to front-load parsing before per-row evaluation.
 */
function parseFormula(formula: string): CachedFormula {
  const cached = expressionCache.get(formula);
  if (cached) return cached;

  const parsed = parser.parse(formula);
  const entry: CachedFormula = { parsed, variables: parsed.variables({ withMembers: true }) };
  if (expressionCache.size >= FORMULA_KEYED_SET_LIMIT) expressionCache.clear();
  expressionCache.set(formula, entry);
  return entry;
}

parser.functions = {
  abs: Math.abs,
  ceil: Math.ceil,
  ceiling: Math.ceil,
  floor: Math.floor,
  round: Math.round,
  roundToCents: (value: number) => Math.round(value * 100) / 100,
  roundToNickels: (value: number) => Math.round(value * 20) / 20,
  roundToQuarters: (value: number) => Math.round(value * 4) / 4,
  min: Math.min,
  max: Math.max,
  sqrt: Math.sqrt,
  pow: Math.pow,
  if: (condition: boolean, trueValue: number = 1, falseValue: number = 0) =>
    condition ? trueValue : falseValue,
};

/**
 * Every function name the formula grammar exposes, read off the parser itself so
 * it cannot drift from the table above. Consumers validate that a user-chosen
 * alias (a Form Derive operand, say) does not shadow a grammar function.
 */
const formulaFunctionNames: readonly string[] = Object.keys(parser.functions);

/**
 * Every constant name the grammar binds — `E`, `PI`, `true`, `false` — read off
 * the parser for the same anti-drift reason as `formulaFunctionNames`, and kept
 * separate because the failure is different in kind. The tokenizer tests
 * `isConst` BEFORE `isName`, so a constant name never becomes a variable at all:
 * the token is replaced by the literal at parse time and the shadowing operand
 * is never consulted. `true + 1` evaluates to `2` whatever the aliased field
 * holds — no parse error, no stale read, just a constant the user cannot see.
 * Consumers validating aliases must reject membership of ALL THREE name lists.
 */
const formulaConstantNames: readonly string[] = Object.keys(parser.consts ?? {});

/**
 * Every operator name the tokenizer resolves, symbolic (`+`, `?`) and named
 * (`length`, `sin`, `not`, `and`, `in`) alike, read off the three live operator
 * maps. Same hazard as the constants and invisible to the usual guard:
 * `Expression.variables()` filters only against `functions`, so an operand
 * aliased `length` survives a `variables ⊆ aliases` check and then resolves to
 * the operator rather than the field, writing `null` forever.
 */
const formulaOperatorNames: readonly string[] = [
  ...Object.keys(parser.unaryOps ?? {}),
  ...Object.keys(parser.binaryOps ?? {}),
  ...Object.keys(parser.ternaryOps ?? {}),
];

/**
 * Converts a cell value to a number for formula evaluation.
 * - Numbers pass through
 * - Strings are parsed (non-numeric become 0)
 * - Everything else becomes 0
 */
function toNumeric(value: ArCellValue): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return Number(value) || 0;
  return 0;
}

/**
 * Builds a numeric context for formula evaluation.
 * Maps variable names (typeIds) to numeric values.
 */
function buildNumericContext(
  variables: string[],
  getValue: (typeId: string) => ArCellValue
): Record<string, number | Record<string, number>> {
  const context: Record<string, number | Record<string, number>> = {};

  for (const variable of variables) {
    const dotIndex = variable.indexOf('.');
    if (dotIndex === -1) {
      context[variable] = toNumeric(getValue(variable));
    } else {
      const root = variable.slice(0, dotIndex);
      const subField = variable.slice(dotIndex + 1);
      const existing = context[root];
      const parent: Record<string, number> =
        existing !== undefined && typeof existing === 'object' ? existing : {};
      parent[subField] = toNumeric(getValue(variable));
      context[root] = parent;
    }
  }

  return context;
}

/**
 * Evaluates a formula expression using cell values from a row.
 *
 * @param formula - Mathematical expression (e.g., "price * quantity")
 * @param cells - Map of typeId to ArCell from the row
 * @returns Evaluated numeric result, or null on error
 *
 * @remarks
 * Available functions:
 * - Basic: abs, ceil, floor, round, min, max, sqrt, pow
 * - Rounding: roundToCents, roundToNickels, roundToQuarters
 * - Conditional: if(condition, trueValue, falseValue)
 *
 * Variable resolution:
 * - Variables reference column typeIds
 * - A cells map resolves through readArCellOperand, the single operand rule
 *   (never `calcInput`, which is the engine's own output cache)
 * - Missing cells default to 0
 * - Non-numeric values convert to 0
 * - Non-finite results (NaN, ±Infinity) return null
 *
 * @example
 * evaluateFormula("price * quantity", {
 *   price: { value: 10 },
 *   quantity: { value: 5 }
 * })
 * // Returns: 50
 *
 * @example
 * evaluateFormula("roundToCents(subtotal * 1.0825)", {
 *   subtotal: { value: 100 }
 * })
 * // Returns: 108.25
 */
function evaluateFormula(
  formula: string,
  cellsOrGetValue: Record<string, ArCell> | ((typeId: string) => ArCellValue)
): number | null {
  if (!formula) return null;

  try {
    const { parsed, variables } = parseFormula(formula);
    const getValue =
      typeof cellsOrGetValue === 'function'
        ? cellsOrGetValue
        : (typeId: string) => readArCellOperand(cellsOrGetValue, typeId);

    const context = buildNumericContext(variables, getValue);
    const result = parsed.evaluate(context);

    return typeof result === 'number' && Number.isFinite(result) ? result : null;
  } catch (error) {
    if (!alarmedFormulas.has(formula)) {
      if (alarmedFormulas.size >= FORMULA_KEYED_SET_LIMIT) alarmedFormulas.clear();
      alarmedFormulas.add(formula);
      console.error('[evaluateFormula] Error:', { formula, error });
    }
    return null;
  }
}

export default evaluateFormula;
export {
  parseFormula,
  toNumeric,
  formulaFunctionNames,
  formulaConstantNames,
  formulaOperatorNames,
};
